import { FormEvent, useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useOutlet } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppSelector, useAppDispatch } from 'hooks/useRedux';
import InfoBar from 'components/InfoBar/InfoBar';
import ErrorBadge from 'components/ErrorBadge/ErrorBadge';
import { authActions, authSelectors } from 'state/auth';
import { workflowActions } from 'state/workflow';
import useIdentifierNavigation from 'hooks/useIdentifierNavigation';
import { UserService } from 'services/Resources';
import { INVALID_EMAIL } from 'constants/validationMessages';
import { emailRegex } from 'constants/validationRules';
import { ConfigurableHyperLinkSelector } from 'state/hyperlink/hyperlink.selectors';
import HealthCheck from 'containers/HealthCheck/HealthCheck';
import ColoredLine from 'components/ColoredLine/ColoredLine';
import { faArrowRight, faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import './LoginView.scss';
import NameHeader from 'components/nameHeader/NameHeader';
import { CheckSquareIcon } from 'assets/icons/CheckSquareIcon';
import { ExclamationSquareIcon } from 'assets/icons/ExclamationSquareIcon';
import WorkflowNavigationButton from 'components/Buttons/WorkflowNavigationButton/WorkflowNavigationButton';

const ERROR_MESSAGE = 'Email or password could not be validated. Please try again';

const LoginView = () => {
  const hyperlinks = useAppSelector(ConfigurableHyperLinkSelector);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const outlet = useOutlet();

  useIdentifierNavigation();

  useEffect(() => {
    window.onpopstate = () => {
      dispatch(authActions.logout());
      dispatch(workflowActions.reset());
      navigate('/', {
        replace: true,
      });
    };
  }, [window]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const wrongCredentials = useAppSelector(authSelectors.wrongCredentialsSelector);
  const isLogin = useAppSelector(authSelectors.isLoginSelector);
  const newTabStatus = useAppSelector(authSelectors.newTabStatus);

  const [showMessage, setShowMessage] = useState(false);
  const [isLoader, setIsLoader] = useState(false);

  const email = getValues('email');
  const password = getValues('password');

  const setUserData = (r: any) => {
    dispatch(authActions.setUser(r));
    clearLoginForm();
  };

  const clearLoginForm = () => {
    setValue('email', '');
    setValue('password', '');
  };

  const getUserData = () => {
    UserService.get(
      {
        params: {
          enabled: undefined,
        },
      },
      setUserData,
      onError,
    );
    navigate('/workflow');
    dispatch(authActions.setWrongCredentials(false));
    setShowMessage(false);
  };

  useEffect(() => {
    if (wrongCredentials) {
      setShowMessage(true);
    }

    if (!wrongCredentials && isLogin) {
      getUserData();
    }
  }, [wrongCredentials, isLogin]);

  useEffect(() => {
    if (wrongCredentials) {
      dispatch(authActions.setWrongCredentials(false));
      setShowMessage(false);
    }
  }, [email, password]);

  const onError = () => {
    dispatch(workflowActions.setError(true));
  };

  const login = handleSubmit(async ({ email, password }) => {
    setIsLoader(true);
    await dispatch(authActions.loggingIn({ username: email, password: password }));
    setIsLoader(false);
    dispatch(authActions.setNewTabStatus(null));
  });

  const getRegistrationWorkflow = () => {
    const newTab = window.open('/register-information', '_blank');

    setTimeout(function () {
      try {
        if (!dispatch || !authActions || typeof authActions !== 'object') return;

        if (newTab === null || newTab.closed || newTab.closed === undefined) {
          dispatch(authActions.setNewTabStatus('ERROR'));
        } else {
          dispatch(authActions.setNewTabStatus('SUCCESS'));
        }
      } catch (e) {
        console.log('Error in opening new tab.');
        console.log(e);
      }
    }, 200);
  };

  const handleForgotPasswordClick = (event: FormEvent) => {
    event.preventDefault();
    navigate('/forgot-password');
  };

  const redBorderError = (formKey: 'email' | 'password') =>
    errors && errors[formKey] && 'border border-danger border-opacity-25 border-2';

  const validationBudge = (formKey: 'email' | 'password') =>
    errors &&
    errors[formKey] && (
      <ErrorBadge
        errors={errors}
        formKey={formKey}
      />
    );

  if (outlet) {
    return <Outlet />;
  }

  return (
    <>
      <div className="w-100">
        <NameHeader
          serviceType={false}
          disableLogoOnClick={true}
        />
        <HealthCheck />
        {newTabStatus === 'SUCCESS' && (
          <Alert className="d-flex justify-content-center alert-success small mb-0">
            <div className="d-flex flex-col align-items-center gap-3">
              <div className="d-flex align-items-center">
                <CheckSquareIcon />
              </div>
              The registration workflow has opened in a new tab. If the new tab didn't automatically
              appear, please check your browser's open tabs to continue.
            </div>
          </Alert>
        )}
        {newTabStatus === 'ERROR' && (
          <Alert
            variant={'danger'}
            className="text-center alert-danger small mb-0"
          >
            <p>
              <span className="me-2">
                <ExclamationSquareIcon />
              </span>
              We were unable to open a new tab to launch your registration process. Please check
              your browser settings to allow pop-ups and try again, or contact support.
            </p>
          </Alert>
        )}
      </div>
      <div className="w-100 login-card-wrapper">
        <div className="card login-card rounded-0 bg-light-purple">
          <ColoredLine />
          <Form onSubmit={login}>
            <Row className="m-0">
              <Col className="px-4 py-4">
                <h2 style={{ fontWeight: 700 }}>ePrescribing Login</h2>
                <p>
                  For XYWAV and XYREM REMS certified prescribers only. Complete the certification by{' '}
                  <a
                    id="complete-certification-here-anchor-login"
                    href={hyperlinks['configurable_hyperlink_1'] || ''}
                    className="text-decoration-none text-purple"
                    target="_blank"
                  >
                    clicking here
                  </a>
                  .
                </p>

                <Form.Group className="mb-3">
                  <Form.Label className="text-black">
                    Email<span className="text-danger"> *</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    id="email-input-login"
                    data-testid="email-input-login"
                    className={`form-control rounded-pill ${redBorderError('email')}`}
                    {...register('email', {
                      required: {
                        value: true,
                        message: 'Email is required',
                      },
                      pattern: {
                        value: emailRegex,
                        message: INVALID_EMAIL,
                      },
                    })}
                  />
                  {validationBudge('email')}
                </Form.Group>
                <Form.Group>
                  <Form.Label className="text-black">
                    Password <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="password"
                    data-testid="password-input-login"
                    className={`form-control rounded-pill ${redBorderError('password')}`}
                    {...register('password', {
                      required: {
                        value: true,
                        message: 'Password is required',
                      },
                    })}
                  />
                  {validationBudge('password')}
                </Form.Group>
                <Link
                  id="forgot-password-anchor-login"
                  onClick={handleForgotPasswordClick}
                  className="text-decoration-none text-purple small"
                  to={'#'}
                >
                  Forgot Password?
                </Link>
                <br />

                <WorkflowNavigationButton
                  text="Login"
                  variant="danger"
                  className="mt-3"
                  disabled={isLoader}
                >
                  {isLoader && (
                    <FontAwesomeIcon
                      icon={faSpinner}
                      color="white"
                      spinPulse={true}
                    />
                  )}
                </WorkflowNavigationButton>

                <div>
                  {showMessage && (
                    <div className="text-danger pt-3">
                      <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className="me-1"
                      />
                      {ERROR_MESSAGE}
                    </div>
                  )}
                </div>
              </Col>
              <Col
                className="p-4 lg-border-start col-auto"
                md="auto"
                s={12}
                xs={12}
                lg="auto"
              >
                <h2 style={{ fontWeight: 700 }}>New User?</h2>

                <WorkflowNavigationButton
                  text="Register Now"
                  variant="danger"
                  className="mt-2"
                  type="button"
                  icon={faArrowRight}
                  iconRight
                  onClick={getRegistrationWorkflow}
                />
              </Col>
            </Row>
          </Form>
        </div>
        <InfoBar center={true}>
          Having trouble logging in?{' '}
          <a
            id="chat-with-us-anchor-login"
            className="text-purple text-decoration-none"
            href={hyperlinks['support_chat_url']}
            target="_blank"
          >
            Chat with us
          </a>{' '}
          or contact support at (855) 863-1355.
        </InfoBar>
      </div>
    </>
  );
};

export default LoginView;
